import { useCallback, useEffect, useMemo, useState } from 'react';

import axios, { type AxiosRequestConfig } from 'axios';
import { type Dayjs } from 'dayjs';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';

import {
    filterByEndDate,
    filterByInstructor,
    filterBySearch,
    filterByStartDate,
    sortByClassName,
    sortByDates,
    sortByInstructors
} from './ClassFilters/filters';
import { type ClassWithInstructors, type Direction, type InstructorWithEmail, type Option, type SortBy, SortOption, TimeFilter } from './types';
import { CLASSES } from '../../../../config/apiPaths';
import { Status } from '../../../../types/apiTypes';
import { getUrls } from '../../../../utils/url';
import AdminAuthContainer from '../../AdminAuthContainer';

const useClasses = () => {
    const { t } = useTranslation('admin_settings');
    const { uuid } = useContainer(AdminAuthContainer);
    const [status, setStatus] = useState<Status>(Status.NotStarted);

    const [classes, setClasses] = useState<ClassWithInstructors[]>([]);
    const [instructors, setInstructors] = useState<InstructorWithEmail[]>([]);

    const [selectedInstructor, setSelectedInstructor] = useState<Option | null>(null);
    const [searchValue, setSearchValue] = useState('');

    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [timeFilter, setTimeFilter] = useState<TimeFilter>(TimeFilter.Current);

    const [sortDirection, setSortDirection] = useState<Direction>(undefined);
    const [sortBy, setSortBy] = useState<SortBy>(undefined);

    const [selectedClass, setSelectedClass] = useState<ClassWithInstructors | null>(null);

    const { enqueueSnackbar } = useSnackbar();

    const { api } = getUrls();

    const fetchClasses = useCallback(async () => {
        if (!uuid) return;

        try {
            setStatus(Status.InProgress);

            const config: AxiosRequestConfig = {
                headers: {
                    token: uuid
                },
                params: { filter: timeFilter }
            };

            const { data } = await axios.get<ClassWithInstructors[]>(`${api}${CLASSES}`, config);

            const instructorMap = {};

            data.forEach((classWithInstructors) => {
                classWithInstructors.instructors.forEach((instructor) => {
                    if (instructor.email) {
                        instructorMap[instructor.email.toLocaleLowerCase()] = instructor;
                    }
                });
            });

            setInstructors(Object.values(instructorMap));
            setClasses(data);
        } catch (err) {
            enqueueSnackbar(t('classes.somethingWentWrong'), { variant: 'error' });
        }

        setStatus(Status.Complete);
    }, [uuid, api, timeFilter, enqueueSnackbar, t]);

    useEffect(() => {
        fetchClasses();
    }, [fetchClasses]);

    const filteredClassesByInstructor: ClassWithInstructors[] = useMemo(
        () => filterByInstructor({ classes, selectedInstructor }),
        [classes, selectedInstructor]
    );
    const filteredClassesBySearch: ClassWithInstructors[] = useMemo(
        () => filterBySearch({ classes: filteredClassesByInstructor, searchValue }),
        [filteredClassesByInstructor, searchValue]
    );
    const filteredClassesByStartDate: ClassWithInstructors[] = useMemo(
        () => filterByStartDate({ classes: filteredClassesBySearch, startDate }),
        [filteredClassesBySearch, startDate]
    );
    const filteredClassesByEndDate: ClassWithInstructors[] = useMemo(
        () => filterByEndDate({ classes: filteredClassesByStartDate, endDate }),
        [endDate, filteredClassesByStartDate]
    );

    const clonedClasses = _.cloneDeep(filteredClassesByEndDate);
    const sortedClassesByName: ClassWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.NAME
                ? sortByClassName({ classes: clonedClasses, sort: sortDirection })
                : clonedClasses,
        [clonedClasses, sortBy, sortDirection]
    );
    const sortedClassesByDates: ClassWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.DATES
                ? sortByDates({ classes: sortedClassesByName, sort: sortDirection })
                : sortedClassesByName,
        [sortedClassesByName, sortBy, sortDirection]
    );
    const filteredClasses: ClassWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.INSTRUCTOR
                ? sortByInstructors({ classes: sortedClassesByDates, sort: sortDirection })
                : sortedClassesByDates,
        [sortedClassesByDates, sortBy, sortDirection]
    );

    return {
        endDate,
        status,
        startDate,
        instructors,
        searchValue,
        setEndDate,
        setStartDate,
        setSearchValue,

        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,

        timeFilter,
        setTimeFilter,
        filteredClasses,
        selectedInstructor,
        setSelectedInstructor,

        classes,
        setClasses,
        fetchClasses,

        selectedClass,
        setSelectedClass,

        uuid
    };
};

const ClassesContainer = createContainer(useClasses);

export default ClassesContainer;
